// SMS Smart Encoding - Transliterates Unicode glyphs to GSM-compatible characters
// Based on Twilio's Smart Encoding character list:
// https://www.twilio.com/docs/messaging/services/smart-encoding-char-list

const map = {};

const addAll = (codePoints, replacement) => {
  codePoints.forEach(cp => { map[String.fromCodePoint(cp)] = replacement; });
};

// Double quotes
addAll([0x00AB, 0x00BB, 0x201C, 0x201D, 0x02BA, 0x02EE, 0x201F, 0x275D,
  0x275E, 0x301D, 0x301E, 0xFF02, 0x201E], '"');

// Single quotes / apostrophes
addAll([0x2018, 0x2019, 0x02BB, 0x02C8, 0x02BC, 0x02BD, 0x02B9, 0x201B,
  0xFF07, 0x00B4, 0x02CA, 0x0060, 0x02CB, 0x275B, 0x275C, 0x0313, 0x0314,
  0xFE10, 0xFE11], "'");

// Forward slash
addAll([0x00F7, 0x29F8, 0x0337, 0x0338, 0x2044, 0x2215, 0xFF0F], '/');

// Backslash
addAll([0x29F9, 0x29F5, 0x20E5, 0xFE68, 0xFF3C], '\\');

// Fractions
map[String.fromCodePoint(0x00BC)] = '1/4';
map[String.fromCodePoint(0x00BD)] = '1/2';
map[String.fromCodePoint(0x00BE)] = '3/4';

// Underscore
addAll([0x0332, 0xFF3F, 0x2017], '_');

// Pipe |
addAll([0x20D2, 0x20D3, 0x2223, 0xFF5C, 0x23B8, 0x23B9, 0x23D0, 0x239C,
  0x239F], '|');

// Dash / hyphen
addAll([0x23BC, 0x23BD, 0x2015, 0xFE63, 0xFF0D, 0x2010, 0x2022, 0x2043,
  0x2014, 0x2013], '-');

// At sign @
addAll([0xFE6B, 0xFF20], '@');

// Dollar $
addAll([0xFE69, 0xFF04], '$');

// Exclamation !
addAll([0x01C3, 0xFE15, 0xFE57, 0xFF01], '!');

// Hash #
addAll([0xFE5F, 0xFF03], '#');

// Percent %
addAll([0xFE6A, 0xFF05], '%');

// Ampersand &
addAll([0xFE60, 0xFF06], '&');

// Comma ,
addAll([0x201A, 0x0326, 0xFE50, 0x3001, 0xFE51, 0xFF0C, 0xFF64], ',');

// Open parenthesis (
addAll([0x2768, 0x276A, 0xFE59, 0xFF08, 0x27EE, 0x2985], '(');

// Close parenthesis )
addAll([0x2769, 0x276B, 0xFE5A, 0xFF09, 0x27EF, 0x2986], ')');

// Asterisk *
addAll([0x204E, 0x2217, 0x229B, 0x2722, 0x2723, 0x2724, 0x2725, 0x2731,
  0x2732, 0x2733, 0x273A, 0x273B, 0x273C, 0x273D, 0x2743, 0x2749, 0x274A,
  0x274B, 0x29C6, 0xFE61, 0xFF0A], '*');

// Plus +
addAll([0x02D6, 0xFE62, 0xFF0B], '+');

// Period .
addAll([0x3002, 0xFE52, 0xFF0E, 0xFF61], '.');

// Colon :
addAll([0x02D0, 0x02F8, 0x2982, 0xA789, 0xFE13, 0xFF1A], ':');

// Semicolon ;
addAll([0x204F, 0xFE14, 0xFE54, 0xFF1B], ';');

// Less than <
addAll([0xFE64, 0xFF1C, 0x2039], '<');

// Equals =
addAll([0x0347, 0xA78A, 0xFE66, 0xFF1D], '=');

// Greater than >
addAll([0xFE65, 0xFF1E, 0x203A], '>');

// Question mark ?
addAll([0xFE16, 0xFE56, 0xFF1F], '?');

// Caret ^
addAll([0x02C6, 0x0302, 0xFF3E, 0x1DCD], '^');

// Open brace {
addAll([0x2774, 0xFE5B, 0xFF5B], '{');

// Close brace }
addAll([0x2775, 0xFE5C, 0xFF5D], '}');

// Open bracket [
addAll([0xFF3B], '[');

// Close bracket ]
addAll([0xFF3D], ']');

// Tilde ~
addAll([0x02DC, 0x02F7, 0x0303, 0x0330, 0x0334, 0x223C, 0xFF5E], '~');

// Double exclamation
map[String.fromCodePoint(0x203C)] = '!!';

// Ellipsis
map[String.fromCodePoint(0x2026)] = '...';

// Fullwidth digits 0-9
for (let i = 0; i <= 9; i++) {
  map[String.fromCodePoint(0xFF10 + i)] = String(i);
}

// Fullwidth uppercase A-Z
for (let i = 0; i < 26; i++) {
  map[String.fromCodePoint(0xFF21 + i)] = String.fromCharCode(65 + i);
}

// Fullwidth lowercase a-z
for (let i = 0; i < 26; i++) {
  map[String.fromCodePoint(0xFF41 + i)] = String.fromCharCode(97 + i);
}

// Small capital letters (IPA) → uppercase equivalents
const smallCaps = [
  [0x1D00, 'A'], [0x0299, 'B'], [0x1D04, 'C'], [0x1D05, 'D'], [0x1D07, 'E'],
  [0xA730, 'F'], [0x0262, 'G'], [0x029C, 'H'], [0x026A, 'I'], [0x1D0A, 'J'],
  [0x1D0B, 'K'], [0x029F, 'L'], [0x1D0D, 'M'], [0x0274, 'N'], [0x1D0F, 'O'],
  [0x1D18, 'P'], [0x0280, 'R'], [0xA731, 'S'], [0x1D1B, 'T'], [0x1D1C, 'U'],
  [0x1D20, 'V'], [0x1D21, 'W'], [0x028F, 'Y'], [0x1D22, 'Z'],
];
smallCaps.forEach(([cp, repl]) => { map[String.fromCodePoint(cp)] = repl; });

// Whitespace → regular space
addAll([0x00A0, 0x2000, 0x2001, 0x2002, 0x2003, 0x2004, 0x2005, 0x2006,
  0x2007, 0x2008, 0x2009, 0x200A, 0x202F, 0x205F, 0x3000], ' ');

// Zero-width / invisible characters → remove
addAll([0x200B, 0xFEFF, 0x2060, 0x2028, 0x2029], '');

// Control characters → remove
addAll([0x008D, 0x009F, 0x0080, 0x0090, 0x009B, 0x0010, 0x0009, 0x0000,
  0x0003, 0x0004, 0x0017, 0x0019, 0x0011, 0x0012, 0x0013, 0x0014], '');

// Build regex from all map keys
const escaped = Object.keys(map)
  .map(c => c.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
  .join('|');
const pattern = new RegExp(escaped, 'gu');

/**
 * Transliterate Unicode glyphs to GSM-compatible characters for SMS.
 * Replaces curly quotes, em dashes, fullwidth characters, etc.
 * with their plain ASCII equivalents.
 */
export function transliterateForSMS(text) {
  if (!text) return text;
  return text.replace(pattern, match => map[match] ?? match);
}
